import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Search, RefreshCw } from "lucide-react";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);

  const reload = (query) => {
    setBusy(true);
    api.get(`/admin/members${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setMembers(r.data || []))
      .catch(() => setMembers([]))
      .finally(() => setBusy(false));
  };

  const resync = () => {
    setResyncing(true);
    setResyncResult(null);
    api.post("/admin/subscriptions/resync", {})
      .then((r) => {
        setResyncResult(r.data || null);
        reload(q);
      })
      .catch((e) => setResyncResult({ error: e?.response?.data?.detail || "Re-sync failed" }))
      .finally(() => setResyncing(false));
  };

  useEffect(() => { reload(""); }, []);

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="label-eyebrow mb-2">Admin · Members</p>
          <h1 className="font-serif text-4xl text-[#2D322B] leading-tight">Member directory.</h1>
        </div>
        <button
          onClick={resync}
          disabled={resyncing}
          data-testid="resync-subscriptions-btn"
          className="btn-secondary !py-2 !px-4 inline-flex items-center gap-2 shrink-0 disabled:opacity-60"
          title="Reconcile all member subscription states against Stripe (use after missed webhook events)"
        >
          <RefreshCw className={`w-4 h-4 ${resyncing ? "animate-spin" : ""}`} />
          {resyncing ? "Re-syncing…" : "Re-sync from Stripe"}
        </button>
      </div>

      {resyncResult && (
        <div
          data-testid="resync-result"
          className="mb-6 bg-white border border-[#E8DFD0] rounded-xl p-4 text-sm"
        >
          {resyncResult.error ? (
            <p className="text-[#C05C42]" data-testid="resync-error">{resyncResult.error}</p>
          ) : (
            <div>
              <p className="text-[#2D322B]">
                Scanned <strong>{resyncResult.scanned}</strong> member(s) ·{" "}
                <strong className="text-[#3F6B4C]">{resyncResult.updated}</strong> updated
                {resyncResult.errors?.length ? <span className="text-[#C05C42]"> · {resyncResult.errors.length} error(s)</span> : null}
              </p>
              {resyncResult.changes?.length > 0 && (
                <ul className="mt-3 space-y-1.5 text-xs text-[#5C6359]">
                  {resyncResult.changes.map((c) => (
                    <li key={c.user_id} data-testid={`resync-change-${c.user_id}`}>
                      <span className="text-[#2D322B]">{c.email}</span>:{" "}
                      {c.before?.status || "—"} → <strong className="text-[#3F6B4C]">{c.after?.status || "—"}</strong>
                      {c.before?.plan !== c.after?.plan && (
                        <span> · plan {c.before?.plan || "—"} → {c.after?.plan || "—"}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {resyncResult.updated === 0 && (
                <p className="text-xs text-[#9A8F7E] mt-2">All member states already match Stripe.</p>
              )}
            </div>
          )}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); reload(q); }}
        className="flex items-center gap-3 mb-6 bg-white border border-[#E8DFD0] px-4 py-2"
      >
        <Search className="w-4 h-4 text-[#9A8F7E]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email…"
          data-testid="members-search"
          className="flex-1 bg-transparent outline-none text-[#2D322B] py-1"
        />
        <button type="submit" disabled={busy} data-testid="members-search-submit" className="text-xs uppercase tracking-[2px] text-[#C05C42] hover:text-[#A04A33]">
          {busy ? "…" : "Search"}
        </button>
      </form>

      <div className="bg-white border border-[#E8DFD0] overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E8DFD0] bg-[#F1ECE4]">
          <p className="label-eyebrow col-span-4">Member</p>
          <p className="label-eyebrow col-span-3">Email</p>
          <p className="label-eyebrow col-span-2">Plan</p>
          <p className="label-eyebrow col-span-2">Status</p>
          <p className="label-eyebrow col-span-1 text-right">Credits</p>
        </div>
        {members.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-[#9A8F7E]" data-testid="members-empty">No members match.</div>
        ) : (
          members.map((m) => (
            <div key={m.id} data-testid={`members-row-${m.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E8DFD0] last:border-b-0 items-center">
              <div className="col-span-4">
                <p className="font-serif text-base text-[#2D322B]">
                  {m.name || "—"} {m.is_admin && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#C05C42] border border-[#C05C42] px-2 py-0.5">Admin</span>}
                </p>
                <p className="text-xs text-[#9A8F7E] mt-1">Joined {formatDate(m.created_at)}</p>
              </div>
              <p className="col-span-3 text-sm text-[#5C6359] truncate">{m.email}</p>
              <p className="col-span-2 text-sm text-[#2D322B] capitalize">{m.plan || "seed"}</p>
              <p className="col-span-2 text-xs text-[#5C6359]">{m.stripe_subscription_status || "—"}</p>
              <p className="col-span-1 text-sm text-right text-[#2D322B]">{m.credit_balance ?? 0}</p>
            </div>
          ))
        )}
      </div>
    </AdminLayout>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}
